import React, { useState } from "react";
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  MoreVert,
  PieChartOutline,
  Telegram,
  Visibility,
  InterpreterMode,
  Cancel,
  Delete,
} from "@mui/icons-material";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import "./survey-card.css";
import SlackDateTimePicker from "../schedule-slack/SlackDateTimePickerWithNav";
import DisplaySchedule from "../schedule-slack/DisplaySchedule";
import { useTranslation } from "react-i18next";
import ErrorBoundary from "../../../../azure_auth/ErrorBoundary";

const createOptions = (name, translation) => ({ name, translation });

/**
 * SurveySlackCardItem:
 * - Recibe `isUserWhitelisted` y `tenantId` ya calculados por el padre.
 * - No se conecta a Redux.
 */
const SurveySlackCardItem = ({
  item,
  sending,
  sendSurveyToTeam,
  deleteSurvey,
  isUserWhitelisted,
  tenantId,
}) => {
  const { t } = useTranslation();

  /* ------------ UI local -------------------- */
  const [anchorEl, setAnchorEl] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbarMsg,   setSnackbarMsg]   = useState("");
  /* ------------ Logs opcionales ------------- */
  console.log("********** isUserWhitelisted (child):", isUserWhitelisted);

  /* ------------ Opciones del menú ----------- */
  const options = [createOptions("Delete", t("surveys.delete"))];

  /* ------------ Handlers -------------------- */
  const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = (option = "") => {
    setAnchorEl(null);
    if (option === "Delete") setConfirmOpen(true);
  };
  const handleConfirmClose = () => setConfirmOpen(false);
  const handleConfirmAgree = () => {
    deleteSurvey(item.id);
    setConfirmOpen(false);
  };

  /* ------------ Schedule activo ------------- */
  const getActiveSchedule = (schedules) =>
    Array.isArray(schedules) ? schedules.find((s) => s.state) || false : false;
  const activeSchedule = getActiveSchedule(item.schedules);

  return (
    <ErrorBoundary sectionName={`SurveySlackCardItem-${item.id}`}>
      <div className="h-100">
        {/* ---------- Tarjeta principal ---------- */}
        <Card className="h-100 position-relative">
          <CardHeader
            action={
              <>
                <IconButton onClick={handleOpenMenu}>
                  <MoreVert />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => handleCloseMenu()}
                >
                  {options.map((opt, idx) => (
                    <MenuItem
                      key={idx}
                      onClick={() => handleCloseMenu(opt.name)}
                    >
                      {opt.translation}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            }
            title={item.title}
            subheader={item.description}
          />
          <CardContent>
            <Typography gutterBottom color="text.secondary">
              {t("surveys.created")}:{" "}
              <b>{format(new Date(item.createdAt), "MM/dd/yyyy")}</b>
            </Typography>
            <Typography gutterBottom color="text.secondary">
              {t("surveys.modified")}:{" "}
              <b>{format(new Date(item.updatedAt), "MM/dd/yyyy")}</b>
            </Typography>
            <Typography variant="body2" color="text.info">
              {t("surveys.responses")}:{" "}
              <b>{item.numberOfResponses || 0}</b>
            </Typography>
            {activeSchedule && <DisplaySchedule schedule={activeSchedule} />}
            {sending[item.id] && (
              <Typography
                variant="body2"
                color="text.info"
                className="d-flex align-items-center"
              >
                <CircularProgress size={16} />
                &nbsp;{t("sending")}...
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* ---------- Acciones inferiores ---------- */}
        <div className="card-action-bottom border-top">
          <div className="d-flex justify-content-evenly animateHover">
            {/* Ver encuesta */}
            <Tooltip title={t("survey.show")}>
              <Link to={`/surveys-show/${item.id}`}>
                <IconButton>
                  <Visibility />
                </IconButton>
              </Link>
            </Tooltip>

            {/* Enviar a equipo */}
            <Tooltip title={t("survey.sendTeam")}>
              <span>
                <IconButton
                  disabled={!tenantId || sending[item.id]}
                  onClick={() => sendSurveyToTeam(item.id)}
                >
                  <Telegram />
                </IconButton>
              </span>
            </Tooltip>

            {/* Enviar a Batabot (solo whitelisted) */}
            {isUserWhitelisted && (
              <Tooltip title="Send Batabot">
                <span>
                  <IconButton
                    disabled={!tenantId || sending[item.id]}
                    onClick={() => sendSurveyToTeam(item.id, true)}
                    sx={{ color: "red" }}
                  >
                    <InterpreterMode />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Resultados */}
            <Link to={`/charts/${item.id}`}>
              <Tooltip title={t("item.results")}>
                <IconButton>
                  <PieChartOutline />
                </IconButton>
              </Tooltip>
            </Link>

            {/* Schedule (crear o editar) */}
            {!activeSchedule ? (
              <SlackDateTimePicker
                surveyId={item.id}
                tenantId={tenantId}
                setOpenSnackbar={setOpenSnackbar}
                setSnackbarMsg={setSnackbarMsg}
              />
            ) : (
              <SlackDateTimePicker
                surveyId={item.id}
                tenantId={tenantId}
                scheduleId={activeSchedule.id}
                days={activeSchedule.days}
                hours={activeSchedule.hours}
                minutes={activeSchedule.minutes}
                setOpenSnackbar={setOpenSnackbar}
                setSnackbarMsg={setSnackbarMsg}
              />
            )}

          </div>
        </div>

        {/* ---------- Diálogo confirmación delete ---------- */}
        <Dialog open={confirmOpen} onClose={handleConfirmClose}>
          <DialogTitle>{t("dialog.deleteTitle", "Delete survey")}</DialogTitle>
          <DialogContent>
            <Typography>
              {t(
                "dialog.deleteMessage",
                `Are you sure you want to delete the survey "${item.title}"?`
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              startIcon={<Cancel fontSize="small" />}
              onClick={handleConfirmClose}
            >
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button
              startIcon={<Delete fontSize="small" />}
              color="error"
              onClick={handleConfirmAgree}
            >
              {t("dialog.delete", "Delete")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ---------- Snackbar de éxito --------------------- */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          onClose={() => setOpenSnackbar(false)}
        >
          <Alert
            severity="success"
            onClose={() => setOpenSnackbar(false)}
            sx={{ width: "100%" }}
          >
            {snackbarMsg}
          </Alert>
        </Snackbar>
      </div>
    </ErrorBoundary>
  );
};

export default SurveySlackCardItem;
